import { Query } from '@apollo/client/react/components';
import {
  GET_PROJECT_TEMPLATE_FORM,
  GET_PROJECT_TEMPLATE_ID_LIST
} from '@nile/banyan/src/Graphql/Query/templates';
import {
  AccessControl,
  ALL_MODULES,
  COLOR_MAP,
  COMMON_CONSTANTS,
  CustomMenu,
  FeatureGuide,
  getTranslatedText,
  preFormatForm,
  ProjectConstraints,
  PSA_CONSTANTS,
  toast,
  useActivityTracker,
  validateForm,
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator,
  WarningModal
} from '@nile/components';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';
import WorklogHoursSection from '@nile/components/src/Containers/Worklog/WorklogHoursSection';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';
import { dompurifier, EventEmitter } from '@nile/components/src/Utils/helpers';
import { TicketClient } from '@nile/graphql';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { useProjectFormStore } from '@nile/mangifera/src/StoreManager/store/ProjectFormStore';
import { SVGIcon } from '@superops/super-icons';
import {
  Avatar,
  Button,
  Heading,
  Page,
  Spinner,
  Dropdown,
  Badge,
  Link,
  TextStyle,
  ButtonGroup,
  Modal
} from '@superops/ui-design-system';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { omit, sortBy } from 'lodash';
import nanoid from 'nanoid';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import Select from 'react-select';
import shortid from 'shortid';
import styled from 'styled-components';
import {
  CreateMilestoneTaskCard,
  MilestoneCard,
  OtherTasksCard,
  ProjectDescription,
  ProjectDetail
} from '../Cards';

const CustomMenuWithActions = (allProps) => {
  const ticketContext = useContext(TabContext);

  return (
    <CustomMenu
      {...allProps}
      actionComponent={
        <Button
          plain
          onClick={() => {
            ticketContext.openTab({
              module: 'ONBOARD',
              url: `/onboard/projecttemplate/${shortid.generate()}/new`
            });
          }}
          icon={<SVGIcon name="Add" color={COLOR_MAP.blue} />}
        >
          <FormattedMessage id="client.details.contract.createTemplate" />
        </Button>
      }
    />
  );
};

const ProjectForm = (props) => {
  const { actions, taskTemplates = [] } = props;

  const ticketId = props.activeTab?.data?.ticketId;
  const module = props.activeTab?.data?.module || 'TICKET';

  const intl = useIntl();
  const context = useContext(TabContext);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [submitActionLoading, setSubmitActionLoading] = useState(false);
  const [templateWarningModal, setTemplateWarningModal] = useState(false);

  // uncomment close warning modal when user is closing the tab
  // const [closeWarningModal, setCloseWarningModal] = useState(false);

  const editorConfig = useMemo(
    () => ({
      id: `PROJECT_FORM_${module}_${ticketId}`,
      name: `PROJECT_CREATE`
    }),
    [ticketId, module]
  );

  const { template, updateTemplate, resetTemplate } = useSuperEditor(
    editorConfig
  );

  const handleProjectChange = useProjectFormStore((s) => s.handleProjectChange);
  const errors = useProjectFormStore((s) => s.errors);
  const setErrors = useProjectFormStore((s) => s.setErrors);

  const title = useProjectFormStore((s) => s.title);
  const setTitle = useProjectFormStore((s) => s.setTitle);

  const client = useProjectFormStore((s) => s.client);
  const setClient = useProjectFormStore((s) => s.setClient);

  const owner = useProjectFormStore((s) => s.owner);
  const setOwner = useProjectFormStore((s) => s.setOwner);

  const site = useProjectFormStore((s) => s.site);
  const setSite = useProjectFormStore((s) => s.setSite);

  const members = useProjectFormStore((s) => s.members);
  const setMembers = useProjectFormStore((s) => s.setMembers);

  const scheduledStartDate = useProjectFormStore((s) => s.scheduledStartDate);
  const setScheduledStartDate = useProjectFormStore(
    (s) => s.setScheduledStartDate
  );

  const dueDate = useProjectFormStore((s) => s.dueDate);
  const setDueDate = useProjectFormStore((s) => s.setDueDate);

  const status = useProjectFormStore((s) => s.status);
  const setStatus = useProjectFormStore((s) => s.setStatus);

  const addMembers = useProjectFormStore((s) => s.addMembers);
  const setAddMembers = useProjectFormStore((s) => s.setAddMembers);

  const deleteMembers = useProjectFormStore((s) => s.deleteMembers);
  const setDeleteMembers = useProjectFormStore((s) => s.setDeleteMembers);

  const addMilestones = useProjectFormStore((s) => s.addMilestones);
  const setAddMilestones = useProjectFormStore((s) => s.setAddMilestones);

  const addTasks = useProjectFormStore((s) => s.addTasks);
  const setAddTasks = useProjectFormStore((s) => s.setAddTasks);

  const associateTimer = useProjectFormStore((s) => s.associateTimer);
  const setAssociateTimer = useProjectFormStore((s) => s.setAssociateTimer);

  const preventScroll = useProjectFormStore((s) => s.preventScroll);
  const setPreventScroll = useProjectFormStore((s) => s.setPreventScroll);

  const showToClient = useProjectFormStore((s) => s.showToClient);
  const setShowToClient = useProjectFormStore((s) => s.setShowToClient);

  const selectedFormTemplate = useProjectFormStore(
    (s) => s.selectedFormTemplate
  );
  const setSelectedFormTemplate = useProjectFormStore(
    (s) => s.setSelectedFormTemplate
  );

  const resetProjectForm = useProjectFormStore((s) => s.resetProjectForm);

  const timerRef = React.createRef();

  useEffect(() => {
    if (context?.activeTab?.data?.associateTimer) {
      setAssociateTimer(true);
    }
    // uncomment close warning modal when user is closing the tab
    // EventEmitter.subscribe('closeProjectCreationTab', (data) => {
    //   setCloseWarningModal(true);
    // });
    // return () => {
    //   EventEmitter.unsubscribe('closeProjectCreationTab');
    // };
  }, []);

  useEffect(() => {
    if (context?.activeTab?.data?.id === 'ticket-to-project') {
      setTitle(title || props.activeTab?.data?.title);
      setClient(client || props.activeTab?.data?.client);
      setSite(site || props.activeTab?.data?.site);
      //update the content of project template with the description from ticket
      updateTemplate({
        content:
          template?.id === ticketId && template?.content
            ? template?.content
            : props.activeTab?.data?.description
      });
    }
  }, []);

  const SETTER_MAP = {
    title: setTitle,
    client: setClient,
    owner: setOwner,
    site: setSite,
    members: setMembers,
    scheduledStartDate: setScheduledStartDate,
    dueDate: setDueDate,
    status: setStatus,
    addMembers: setAddMembers,
    deleteMembers: setDeleteMembers,
    addMilestones: setAddMilestones,
    addTasks: setAddTasks,
    associateTimer: setAssociateTimer,
    preventScroll: setPreventScroll,
    showToClient: setShowToClient,
    selectedFormTemplate: setSelectedFormTemplate
  };

  const handleChange = (val, target) => {
    const setter = SETTER_MAP[target];
    setter(val);
  };

  const togglePreventScroll = (bool) => {
    setPreventScroll(bool);
  };

  const handleMilestoneCreation = () => {
    const newMilestone = {
      name: '',
      addTasks: [],
      milestoneId: nanoid(),
      milestoneOrder: addMilestones.length + 1
    };

    const currentMileStones = addMilestones || [];
    setAddMilestones([...currentMileStones, newMilestone]);
    togglePreventScroll(true);
  };

  const handleOtherTasksAddition = (task) => {
    const currentTasks = addTasks || [];
    const updatedTasks = [...currentTasks];
    updatedTasks.push({ ...task, taskOrder: (addTasks?.length ?? 0) + 1 });
    setAddTasks(updatedTasks);
    togglePreventScroll(true);
  };

  const handleOtherTaskTemplateAddition = (tasks) => {
    const currentTasks = addTasks || [];
    const updatedTasks = [
      ...currentTasks,
      ...tasks.map((t, index) => ({
        ...t,
        taskOrder: (currentTasks?.length ?? 0) + index + 1
      }))
    ];
    setAddTasks(updatedTasks);
  };

  const handleMilestoneUpdate = (_, obj) => {
    const currentMileStones = addMilestones || [];
    const updatedMilestones = [...currentMileStones].map((m) => {
      if (m.milestoneId === obj.milestoneId) {
        return {
          ...obj,
          addTasks: obj.addTasks.map((item, i) => ({
            ...item,
            taskOrder: i + 1
          }))
        };
      }

      return m;
    });

    setAddMilestones(updatedMilestones);
  };

  const handleMilestoneDeletion = (milestoneId) => {
    const currentMileStones = addMilestones || [];
    const updatedMilestones = currentMileStones
      .filter((m) => m.milestoneId !== milestoneId)
      .map((m, i) => ({ ...m, milestoneOrder: i + 1 }));

    setAddMilestones(updatedMilestones);
  };

  const resetErrors = (type) => {
    const updatedErrors = { ...errors };
    delete updatedErrors[type];
    setErrors(updatedErrors);
  };

  const createProject = () => {
    if (actions?.primaryAction) {
      const reqPayload = {
        title,
        status,
        scheduledStartDate,
        dueDate,
        client,
        site,
        owner,
        addMembers,
        addMilestones: addMilestones.map((milestone) =>
          omit(milestone, 'milestoneId')
        ),
        addTasks,
        showToClient
      };
      const templateContent =
        template?.content === '<p><br></p>' ? '' : template?.content || '';
      const dummyForm = {
        ...reqPayload,
        content: templateContent
      }; // hack to check added for empty state in editor
      const validationResult = validateForm(
        preFormatForm(dummyForm),
        ProjectConstraints
      );
      if (validationResult) {
        setSubmitActionLoading(false);
        setErrors(validationResult);
        return;
      }
      // TEMPORARY CHANGES: (AFTER CHANGES IN BE) -> SEND DESCRIPTION IN SAME CALL
      if (template?.attachments?.length || templateContent) {
        reqPayload.comment = {
          content: dompurifier(templateContent),
          mentions: template?.mentions || [],
          attachments: template?.attachments?.map(
            ({ fileName, originalFileName, fileSize }) => {
              return { fileName, originalFileName, fileSize };
            }
          ),
          inlineImages: template?.inlineImages || []
        };
      }

      if (associateTimer && timerRef.current?.getTimerInputObj) {
        reqPayload.updateWorklogTimer = timerRef.current?.getTimerInputObj;
      }

      const getAssociation = (id, module) =>
        id && module ? { workId: id, module } : undefined;

      const associations = getAssociation(ticketId, module);

      if (associations) {
        reqPayload.associations = associations;
      }
      setSubmitActionLoading(true);
      setErrors(null);
      actions
        .primaryAction({
          variables: { input: reqPayload },
          input: reqPayload
        })
        .then((res) => {
          if (res?.data?.createProject) {
            resetTemplate();
            resetProjectForm();
            toast('success', 'project.newProject.projectCreation.success');
            useActivityTracker({ event: 'Project - Created a project' });
            setSubmitActionLoading(false);

            context.closeTab();
            setTimeout(() => {
              context.openTab({
                module: ALL_MODULES.projectDetail,
                data: {
                  projectId: res.data.createProject.projectId,
                  title: res.data.createProject.title
                }
              });
            }, 50);
          }
        });
    }
  };

  const handleProjectTemplateChange = (val = selectedFormTemplate) => {
    setTemplateLoading(true);
    togglePreventScroll(true);
    TicketClient.query({
      query: GET_PROJECT_TEMPLATE_FORM,
      variables: { input: { templateId: val.templateId } },
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    })
      .then((response) => {
        const formData = response?.data?.getProjectContentFromTemplate;

        if (formData) {
          setSelectedFormTemplate(val);
          setTitle(formData.title || '');
          context.updateTab({
            ...context?.activeTab,
            data: {
              ...context?.activeTab?.data,
              description: formData.description || ''
            }
          });
          updateTemplate({
            id: ticketId || nanoid(),
            name: formData.title || '',
            content: formData.description || '',
            placeholdersUsed: '',
            mentions: [],
            attachments: formData.attachments || [],
            inlineImages: []
          });
          setScheduledStartDate(formData.scheduledStartDate || null);
          setDueDate(formData.dueDate || null);
          const mileStoneFromTemplate = formData.milestones;
          if (mileStoneFromTemplate) {
            const formattedMileStone = mileStoneFromTemplate.map(
              (milestone) => ({
                name: milestone.name,
                addTasks: milestone.tasks || [],
                milestoneId: nanoid(),
                milestoneOrder: milestone.milestoneOrder
              })
            );
            setAddMilestones(formattedMileStone);
          } else {
            setAddMilestones([]);
          }
          setAddTasks(formData.tasks || []);
          setTemplateLoading(false);
          setTimeout(() => {
            togglePreventScroll(false);
          }, 500);
        } else {
          togglePreventScroll(false);
          setTemplateLoading(false);
        }
      })
      .catch(() => {
        togglePreventScroll(false);
        setTemplateLoading(false);
      });
  };

  const sortedAddMilestones = sortBy(addMilestones, 'milestoneOrder');

  const handleApplyTemplate = () => {
    handleProjectTemplateChange();
    setTemplateWarningModal(false);
  };

  const handleCancelTemplateModal = () => {
    setTemplateWarningModal(false);
    setSelectedFormTemplate(null);
  };

  // uncomment close warning modal when user is closing the tab
  // const handleSaveAndCloseTab = () => {
  //   setCloseWarningModal(false);
  //   createProject();
  // };

  // const handleDiscardChanges = () => {
  //   setCloseWarningModal(false);
  //   context.closeTab();
  // };

  return (
    <div
      className={`full-height full-width fx g20 white-grey-page tw-p-4 ${
        ticketId ? 'mw1800' : ''
      }`}
    >
      <div className={`${ticketId ? 'cw-320' : 'fw'}`}>
        <Page
          fullWidth
          title={
            <>
              <div className="flex actr tw-gap-x-4">
                <div className="flex actr tw-gap-x-1">
                  <div className="flex">
                    <SVGIcon name="ProjectFilled" size="xl" />
                  </div>
                  <div className="flex">
                    <Heading element="h1">
                      <FormattedMessage id="tickets.activeprojects.create.header" />
                    </Heading>
                  </div>
                </div>

                <div className="flex tw-pt-2">
                  <FeatureGuide
                    featureName="projectmanagement"
                    video
                    onlyIcon
                  />
                </div>

                <AccessControl
                  roleToCheck="projectTemplate.view"
                  render={
                    <Query
                      query={GET_PROJECT_TEMPLATE_ID_LIST}
                      fetchPolicy="cache-and-network"
                    >
                      {({ loading, error, data }) => {
                        if (loading)
                          return <Spinner size="small" color="inkLightest" />;
                        if (error) return `Error! ${error.message}`;

                        let forms = data?.getProjectTemplates || [];
                        forms = forms.map((el) => {
                          return {
                            ...el,
                            value: el.name,
                            label: el.name,
                            name: el.name
                          };
                        });

                        return (
                          <div className="w100 mw-300  flex actr tw-gap-x-2">
                            <Badge
                              status="success"
                              icon={<SVGIcon name="Tasks" />}
                              radial
                            />
                            <div className="full-width ml8">
                              <Dropdown
                                menuPortalTarget={document.body}
                                value={selectedFormTemplate}
                                onChange={(val) => {
                                  if (
                                    !selectedFormTemplate?.templateId &&
                                    ticketId
                                  ) {
                                    setSelectedFormTemplate(val);
                                    setTemplateWarningModal(true);
                                  } else {
                                    handleProjectTemplateChange(val);
                                  }
                                }}
                                placeholder={getTranslatedText(
                                  intl,
                                  'project.choose.template'
                                )}
                                name="forms"
                                options={forms}
                                classNamePrefix="value_selector"
                                components={{
                                  IndicatorSeparator,
                                  DropdownIndicator,
                                  Menu: CustomMenuWithActions
                                }}
                                formatGroupLabel={formatGroupLabel}
                                menuPlacement="auto"
                                closeMenuOnScroll={(e) => {
                                  return (
                                    e.target &&
                                    e.target.classList &&
                                    !(
                                      e.target.classList.value.indexOf(
                                        'menu-list'
                                      ) > -1
                                    )
                                  );
                                }}
                                styles={{
                                  placeholder: (base) => ({
                                    ...base,
                                    fontSize: '14px',
                                    fontWeight: 'auto'
                                  }),
                                  singleValue: (base) => ({
                                    ...base,
                                    fontSize: '14px',
                                    fontWeight: 'auto'
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999
                                  })
                                }}
                                disabled={
                                  submitActionLoading || templateLoading
                                }
                              />
                            </div>
                          </div>
                        );
                      }}
                    </Query>
                  }
                />
              </div>
            </>
          }
          contentHeight="120px"
          primaryAction={
            <div className="fx fx-sb actr">
              <PrimaryActionsHolder>
                <Button
                  plainSecondary
                  disabled={templateLoading}
                  onClick={() => context.closeTab(context.activeTab)}
                >
                  <FormattedMessage id="common.cancel" />
                </Button>
                <CreateMilestoneTaskCard
                  addMilestone={handleMilestoneCreation}
                  handleOtherTasksAddition={handleOtherTasksAddition}
                  handleOtherTaskTemplateAddition={
                    handleOtherTaskTemplateAddition
                  }
                  taskTemplates={taskTemplates}
                  isActionList
                />
                <Button
                  primary
                  id="actionGroupSaveEmail"
                  loading={submitActionLoading}
                  disabled={templateLoading}
                  onClick={createProject}
                  icon={
                    !submitActionLoading && (
                      <SVGIcon name="Save" color={COLOR_MAP.white} />
                    )
                  }
                >
                  <FormattedMessage id="common.save" />
                </Button>
              </PrimaryActionsHolder>
            </div>
          }
        >
          <div className={templateLoading ? 'disabled opacity5' : ''}>
            <div className="inner">
              <ProjectDescription
                title={title}
                status={status}
                handleChange={handleChange}
                resetErrors={resetErrors}
                errors={errors}
                client={client}
                meta={{ titlePlaceholder: 'ticket.newProject.pl.proname' }}
                editorConfig={editorConfig}
                useSuperEditor
                attachmentModule="PROJECT" // For inline images
                uploadParams={{
                  module: 'PROJECT_CONVERSATION_ATTACHMENT' // For file upload
                }}
              />
              <ProjectDetail
                client={client}
                owner={owner}
                site={site}
                members={members}
                addMembers={addMembers}
                deleteMembers={deleteMembers}
                scheduledStartDate={scheduledStartDate}
                dueDate={dueDate}
                errors={errors}
                resetErrors={resetErrors}
                handleChange={handleChange}
                showToClient={showToClient}
                isCreateFromTicket={!!ticketId}
              />
              <SuperSortable
                onDragEnd={(reorderedMilestone) => {
                  const newList = reorderedMilestone.map((m, i) => ({
                    ...m,
                    milestoneOrder: i + 1
                  }));
                  useActivityTracker({
                    event:
                      PSA_CONSTANTS.MIX_PANEL_EVENTS_FOR_REORDER.PROJECT_CREATE
                        .MILESTONE
                  });
                  setAddMilestones(newList);
                }}
                list={sortedAddMilestones}
              >
                {sortedAddMilestones.map((milestone, index) => (
                  <MilestoneCard
                    key={`milestone-${milestone?.milestoneId}`}
                    index={index}
                    milestoneId={milestone?.milestoneId}
                    title={milestone?.name}
                    tasks={sortBy(milestone?.addTasks, 'taskOrder')}
                    updateMilestone={(_, m) => {
                      handleMilestoneUpdate(_, { ...milestone, ...m });
                    }}
                    taskTemplates={taskTemplates}
                    deleteMilestone={() => {
                      handleMilestoneDeletion(milestone.milestoneId);
                    }}
                    errors={
                      errors?.addMilestones?.[0]?.errors?.[index]?.error?.name
                    }
                    preventScroll={preventScroll}
                    onTasksReorder={(reorderedTasks) => {
                      useActivityTracker({
                        event:
                          PSA_CONSTANTS.MIX_PANEL_EVENTS_FOR_REORDER
                            .PROJECT_CREATE.TASK
                      });
                      const updatedMileStones = sortedAddMilestones.map((m) => {
                        const clonedMilestone = { ...m };

                        if (m.milestoneId === milestone.milestoneId) {
                          clonedMilestone.addTasks = reorderedTasks;
                          return clonedMilestone;
                        }

                        return clonedMilestone;
                      });
                      setAddMilestones(updatedMileStones);
                    }}
                  />
                ))}
              </SuperSortable>
              {addTasks?.length > 0 && (
                <OtherTasksCard
                  tasks={sortBy(addTasks, 'taskOrder')}
                  handleOtherTasksAddition={handleOtherTasksAddition}
                  handleOtherTaskTemplateAddition={
                    handleOtherTaskTemplateAddition
                  }
                  taskTemplates={taskTemplates}
                  updateOtherTasks={(otherTasks) => {
                    setAddTasks(otherTasks);
                  }}
                  preventScroll={preventScroll}
                  onTasksReorder={(newOrderedList) => {
                    useActivityTracker({
                      event:
                        PSA_CONSTANTS.MIX_PANEL_EVENTS_FOR_REORDER
                          .PROJECT_CREATE.TASK
                    });
                    setAddTasks(newOrderedList);
                  }}
                />
              )}
            </div>
          </div>
        </Page>
      </div>

      {ticketId && (
        <div className="fx col white-bg w300 hfc br-8 tw-p-4 tw-gap-y-2">
          <TextStyle weight="Bold">
            <FormattedMessage id="label.linked.ticket" />
          </TextStyle>
          <Link removeUnderline url={`#/tickets/${ticketId}/ticket`}>
            {props.activeTab?.data?.title}
          </Link>
        </div>
      )}
      {templateWarningModal && (
        <Modal
          open={templateWarningModal}
          onClose={() => {
            setTemplateWarningModal(false);
            setSelectedFormTemplate(null);
          }}
          variant="confirmation"
          iconSize="xl"
          title={getTranslatedText(intl, 'common.confirmation')}
          primaryAction={{
            content: (
              <span className="fx actr tw-gap-1">
                <SVGIcon name="CircleTickThinOutline" color="#ffffff" />
                <FormattedMessage id="common.applyTemplate" />
              </span>
            ),
            onAction: handleApplyTemplate
          }}
          secondaryActions={{
            content: getTranslatedText(intl, 'common.cancel'),
            onAction: handleCancelTemplateModal
          }}
        >
          <TextStyle>
            <FormattedMessage id="project.template.confirmation.description" />
          </TextStyle>
        </Modal>
      )}

      {/* uncomment close warning modal when user is closing the tab */}
      {/* {closeWarningModal && (
        <Modal
          open={closeWarningModal}
          showCloseButtonForConfirmation
          onClose={() => setCloseWarningModal(false)}
          variant="confirmation"
          iconSize="xl"
          title={getTranslatedText(intl, 'common.confirmation')}
          primaryAction={{
            content: (
              <span className="fx actr tw-gap-1">
                <SVGIcon name="CircleTickThinOutline" color="#ffffff" />
                <FormattedMessage id="common.saveAndClose" />
              </span>
            ),
            onAction: handleSaveAndCloseTab
          }}
          secondaryActions={[
            {
              content: getTranslatedText(intl, 'common.discard.changes'),
              onAction: handleDiscardChanges
            }
          ]}
        >
          <div className="fx col tw-gap-y-2">
            <TextStyle>
              <FormattedMessage id="project.close.confirmation.description" />
            </TextStyle>
            <TextStyle>
              <FormattedMessage id="project.converted.ticketId" />
              <Link
                removeUnderline
                url={`#/tickets/${props.activeTab?.data?.ticketId}/ticket`}
              >{`: #${props.activeTab?.data?.displayId}`}</Link>
            </TextStyle>
          </div>
        </Modal>
      )} */}
      {associateTimer && (
        <WorkLogHourSectionContainer className="fw">
          <WorklogHoursSection
            hideCancel
            editMode
            hideDelete
            noBoxShadow
            onlyManual={associateTimer?.type !== 'AUTOMATIC'}
            timer={associateTimer}
            workItem={associateTimer?.workItem}
            module={associateTimer?.module || null}
            highlightRunningTimer
            forwardedRef={timerRef}
          />
        </WorkLogHourSectionContainer>
      )}
    </div>
  );
};

const Wrapper = styled.div`
  .customReactSelectMenu {
    position: absolute;
    z-index: 1000;
  }
  .value_selector__menu {
    z-index: 1000;
  }
  .Polaris-Page {
    background: var(--sops-white-to-grey-bg) !important;
  }
  .Polaris-Page-Header__TitleActionMenuWrapper {
    width: fit-content;
    flex: none;
  }
  .Polaris-Page-Header__PrimaryActionWrapper {
    flex: 1;
    .project-form-selector {
      min-width: 220px;
      .Superops-Avatar__Image {
        width: 20px;
        height: 20px;
        background: #afcf8d;
      }
      .Superops-Avatar--hasImage {
        background: #afcf8d;
      }
    }
  }
`;

const FormContentContainer = styled.div`
  /* height: calc(100vh - 130px);
  overflow: auto; */
  padding: 0px;
  padding-top: 0 !important;
  /* > div {
    margin-bottom: 30px;
  } */
  .Polaris-Subheading {
    padding-top: 6px;
  }
  .year-input {
    width: 200px;
  }
`;

const PrimaryActionsHolder = styled.div`
  display: grid;
  gap: 15px;
  grid-template-columns: 46px 123px 94px;
`;

const WorkLogHourSectionContainer = styled.div`
  max-width: 450px;
`;

export default withLDConsumer()(withRouter(injectIntl(ProjectForm)));
